/* eslint-disable @typescript-eslint/default-param-last */
import { FloatResponse } from '@frontend/epc-services';
import { getClosestUSDAGuaranteeFee } from '@frontend/pricing-search';
import { partitionArray } from '@frontend/utils';
import Session, {
  IS_VALIDATE,
  ORIGIN_LOCKID,
} from '../../common/services/Session';

type FhaAndVaTypes = Pick<
  FloatResponse.Loan,
  'gfe' | 'miAndFundingFeeFinancedAmount' | 'mipPaidInCashAmount'
>;

type VaTypes = {
  vaLoanData: Pick<
    FloatResponse.Loan['vaLoanData'],
    'fundingFeeExempt' | 'firstTimeUse'
  >;
};

type PropertyValueParamsType = Partial<
  Pick<
    FloatResponse.Loan,
    | 'propertyAppraisedValueAmount'
    | 'propertyEstimatedValueAmount'
    | 'purchasePriceAmount'
  >
>;

export const parseOriginAdjustments = (originalLoanInfo) => {
  const { rateLock } = originalLoanInfo || {};
  const {
    buySideAdjustments,
    buySidePriceRate,
    buySideRate,
    buySidePriceNetBuyPrice,
    buySideRateNetBuyRate,
  } = rateLock || {};

  if (
    [
      buySideAdjustments,
      buySidePriceRate,
      buySideRate,
      buySidePriceNetBuyPrice,
      buySideRateNetBuyRate,
    ].includes(undefined)
  ) {
    return [];
  }

  const mappedAdjustments = buySideAdjustments
    .filter(
      ({ priceAdjustmentType, adjustmentType }) =>
        ['BasePrice', 'BaseRate', 'ProfitMargin'].includes(
          priceAdjustmentType,
        ) ||
        ['CorporatePriceConcession', 'BranchPriceConcession'].includes(
          adjustmentType,
        ),
    )
    .map(({ description, rate, priceAdjustmentType, adjustmentType }) => ({
      adjustmentType:
        priceAdjustmentType === 'ProfitMargin' ? 'Profit' : adjustmentType,
      description,
      price: priceAdjustmentType !== 'BaseRate' ? rate : null,
      rate: priceAdjustmentType === 'BaseRate' ? rate : null,
    }));
  return [
    {
      adjustmentType: 'Final',
      description: 'FINAL',
      price: buySidePriceNetBuyPrice,
      rate: buySideRateNetBuyRate,
    },
    {
      adjustmentType: 'Base',
      description: 'BASE',
      price: buySidePriceRate,
      rate: buySideRate,
    },
    ...mappedAdjustments,
  ];
};

const roundAdjustment = (num) => Number((num || 0).toFixed(3));

const getDuplicateAdjustmentDescriptions = (adjustmentArray) => {
  const descriptions = adjustmentArray
    .filter(
      (adj) =>
        adj?.adjustmentType === 'Adjustment' || adj?.adjustmentType === 'SRP',
    )
    .map((adj) => `${adj?.description}`);
  const descriptionCount = descriptions.reduce((acc, description) => {
    acc[description] = (acc[description] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(descriptionCount).filter(
    (description) => descriptionCount[description] > 1,
  );
};

export const getAdjustmentCompare = (
  leftAdjustments: any[] = [],
  rightAdjustments: any[] = [],
) => {
  if (leftAdjustments.length === 0 || rightAdjustments.length === 0) {
    return { comparissonArray: [], changeCounter: 0 };
  }
  const tempObj = {};
  const hasLeftDuplicateAdj =
    getDuplicateAdjustmentDescriptions(leftAdjustments);
  const hasRightDuplicateAdj =
    getDuplicateAdjustmentDescriptions(rightAdjustments);

  leftAdjustments.forEach((adj) => {
    let key = adj.description;
    if (
      adj.adjustmentType === 'ReLockFeeAdjustment' ||
      adj.adjustmentType === 'CustomPriceAdjustment'
    ) {
      key = `${adj.description}|${adj.price}|${adj.adjustmentType}`;
    }
    if (
      (adj.adjustmentType === 'Adjustment' || adj.adjustmentType === 'SRP') &&
      hasLeftDuplicateAdj.length > 0 &&
      hasLeftDuplicateAdj.includes(`${adj.description}`)
    ) {
      key = `${key}|${adj?.price}|${adj?.rate}|${adj?.adjustmentType}`;
    }
    tempObj[key] = adj;
  });
  rightAdjustments.forEach((adj) => {
    let key = adj.description;
    if (
      adj.adjustmentType === 'ReLockFeeAdjustment' ||
      adj.adjustmentType === 'CustomPriceAdjustment'
    ) {
      key = `${adj.description}|${adj.price}|${adj.adjustmentType}`;
    }
    if (
      (adj.adjustmentType === 'Adjustment' || adj.adjustmentType === 'SRP') &&
      hasRightDuplicateAdj.length > 0 &&
      hasRightDuplicateAdj.includes(`${adj.description}`)
    ) {
      key = `${key}|${adj?.price}|${adj?.rate}|${adj?.adjustmentType}`;
    }
    if (tempObj[key]) {
      tempObj[key] = {
        ...tempObj[key],
        currentPrice: adj.price,
        currentRate: adj.rate,
      };
    } else {
      tempObj[key] = {
        ...adj,
        currentPrice: adj.price,
        currentRate: adj.rate,
      };
      delete tempObj[key].price;
      delete tempObj[key].rate;
    }
  });
  let changeCounter = 0;
  const comparissonArray = Object.values<{
    price: number;
    currentPrice: number;
    rate: number;
    currentRate: number;
    description: string;
    adjustmentType: string;
  }>(tempObj).map((adj) => {
    const histPrice = roundAdjustment(adj.price);
    const currPrice = roundAdjustment(adj.currentPrice);
    const changePrice = Math.abs(histPrice - currPrice);

    const histRate = roundAdjustment(adj.rate);
    const currRate = roundAdjustment(adj.currentRate);
    const changeRate = Math.abs(histRate - currRate);

    if (
      !Session.get(IS_VALIDATE) ||
      (adj.description !== 'All-In (incl. Profit Margins)' &&
        adj.description !== 'FINAL')
    ) {
      if (changeRate !== 0) changeCounter += 1;
      if (changePrice !== 0) changeCounter += 1;
    }

    return {
      ...adj,
      change: changePrice.toFixed(3),
    };
  });
  return { comparissonArray, changeCounter };
};

export const parseRateAdjustmentsValidate = (
  adjustments,
  originContainsLOComp,
) => {
  const [adjsNoSRP, srpAdjs] = partitionArray(
    adjustments,
    ({ adjustmentType }) => adjustmentType !== 'SRP',
  );
  const srpSum: { price: number; rate: number } = srpAdjs.reduce(
    (
      { price: prevPrice, rate: prevRate },
      { price: currentPrice, rate: currentRate },
    ) => ({
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      price: prevPrice + Number(currentPrice),
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      rate: prevRate + Number(currentRate),
    }),
    { price: 0, rate: 0 },
  );
  const baseAdj = adjustments.find(
    ({ adjustmentType }) => adjustmentType === 'Base',
  );
  const finalAdj = adjustments.find(
    ({ adjustmentType }) => adjustmentType === 'Final',
  );
  const [adjsNoSrpNoComp, [loComp]] = partitionArray(
    adjsNoSRP,
    ({ description }) => description !== 'L.O. Compensation',
  );
  if (originContainsLOComp || !loComp) {
    const newBase = {
      ...baseAdj,
      price: Number(baseAdj.price) + srpSum.price,
      rate: Number(baseAdj.rate) + srpSum.rate,
    };
    return [
      newBase,
      ...adjsNoSRP.filter(({ adjustmentType }) => adjustmentType !== 'Base'),
    ];
  }
  const newBaseWithoutSrpOrComp = {
    ...baseAdj,
    price: Number(baseAdj.price) + srpSum.price,
    rate: Number(baseAdj.rate) + srpSum.rate,
  };
  const newFinalWithoutSrpOrComp = {
    ...finalAdj,
    price: Number(finalAdj.price) - Number(loComp.price),
    rate: Number(finalAdj.rate),
  };
  return [
    newBaseWithoutSrpOrComp,
    ...adjsNoSrpNoComp.filter(
      ({ adjustmentType }) =>
        adjustmentType !== 'Base' && adjustmentType !== 'Final',
    ),
    newFinalWithoutSrpOrComp,
  ];
};
export const checkPricingStatus = (
  programID,
  rateID,
  rateValue,
  pricingResults = {},
  changedNoteRate = false,
) => {
  const { loanPrograms } = pricingResults as any;
  const program = loanPrograms?.find(
    ({ programID: pricingProgramID }) => pricingProgramID === programID,
  );
  if (program) {
    if (program.deleted === 0) {
      const rateIDMatch = program.rateDetails?.find(
        ({ rateDataID }) => rateDataID === rateID,
      );
      const rateValueMatch = getMatchingRate(
        program.rateDetails,
        rateValue,
        true,
      );
      const { rateDataID } = changedNoteRate
        ? rateValueMatch || {}
        : rateIDMatch || rateValueMatch || {};
      if (rateDataID) {
        return { program, rateDataID, status: 'eligible' };
      }
      return { program, rateDataID: null, status: 'ineligibleRate' };
    }
    return { program, rateDataID: rateID, status: 'ineligible' };
  }
  return { program: null, rateDataID: null, status: 'unavailable' };
};

export const getSnapshotProgramInfo = (lockRequests) => {
  const currentSnapshot = lockRequests.find(
    ({ id }) => id === Session.get(ORIGIN_LOCKID),
  );
  const activeLockSnapshot = lockRequests.find(
    ({ requestedStatus }) => requestedStatus === 'Locked',
  );
  const snapshot = currentSnapshot || activeLockSnapshot || {};
  const { snapshotFields = [] } = snapshot;
  const { value: snapshotProgramName = '' } =
    snapshotFields.find(({ fieldId }) => fieldId === '2866') || {};
  const { value: snapshotInvestorName = '' } =
    snapshotFields.find(({ fieldId }) => fieldId === '2278') || {};
  return `${snapshotInvestorName} ${snapshotProgramName}`;
};

const getMatchingRate = (
  rateDetails: any[] = [],
  selectedRate,
  tolerance = false,
) => {
  if (tolerance) {
    return rateDetails.find(
      ({ rate }) => Math.abs(rate - selectedRate) < 0.0001,
    );
  }
  return rateDetails.find(({ rate }) => rate === selectedRate);
};

export const findBestProgram = (
  loanPrograms = [],
  selectedRate,
  selectedTerm,
) => {
  const matchingPrograms = loanPrograms.filter(
    ({ deleted, term, rateDetails }) =>
      deleted === 0 &&
      term === selectedTerm &&
      getMatchingRate(rateDetails, selectedRate),
  );

  const bestProgram = matchingPrograms.reduce((prev: any, curr: any) => {
    if (
      prev === null ||
      getMatchingRate(prev.rateDetails, selectedRate).price >
        getMatchingRate(curr.rateDetails, selectedRate).price
    ) {
      return curr;
    }
    return prev;
  }, null);
  const {
    programID: bestProgramID,
    rateDetails: bestRateDetails,
    wcpPolicyApplied,
  } = bestProgram || {};
  const bestRateID = getMatchingRate(bestRateDetails, selectedRate)?.rateDataID;
  const lockEffectiveDate = getMatchingRate(
    bestRateDetails,
    selectedRate,
  )?.lockEffectiveDate;
  return { bestProgramID, bestRateID, lockEffectiveDate, wcpPolicyApplied };
};

const calculateGovtFeeFactor = (feeAmount: number, baseLoanAmount: number) => {
  if (!feeAmount || !baseLoanAmount) return 1;
  return parseFloat(((feeAmount / baseLoanAmount) * 100).toFixed(6));
};

export const getGoveFeesForFHAandVA = (
  summaryDetails?: any,
): Partial<FhaAndVaTypes> => {
  if (!summaryDetails?.lien?.fha && !summaryDetails?.lien?.va) return {};

  // EPPS-105092: paid in cash under $1 means the fee is fully financed, so
  // leave the paid-in-cash lock off and let Encompass derive it.
  const lockField = !(summaryDetails?.loan?.feeAmountPaidinCash < 1);

  return {
    gfe: {
      lockField, // fundingAmount and mipPaidInCash will not populate unless this is true
      fundingAmount: summaryDetails?.loan?.feeAmount,
    },
    miAndFundingFeeFinancedAmount: summaryDetails?.loan?.feeAmountFinanced,
    mipPaidInCashAmount: summaryDetails?.loan?.feeAmountPaidinCash,
  };
};

export const getGoveFeesForUSDA = (summaryDetails: any) => {
  if (!summaryDetails?.lien?.usda) return {};

  const govtFeeFactor = calculateGovtFeeFactor(
    summaryDetails?.loan?.feeAmount,
    summaryDetails?.loan?.baseLoanAmount,
  );

  return {
    financedAllGuaranteeFeePercent: getClosestUSDAGuaranteeFee(govtFeeFactor),
    financingType: 'FinancingPortion',
    financedPortionGuaranteeFeePercent: summaryDetails?.loan?.feeAmountFinanced,
  };
};

const getRiskAssessmentTypeString = (riskAssessmentType: 1 | 2 | 3 | 4 | 5) => {
  if (!riskAssessmentType) return '';
  const riskAssessmentMap = {
    1: 'DU',
    2: 'LPA',
    3: 'LQA',
    4: 'Manual Underwriting',
    5: 'Other',
  };
  return riskAssessmentMap[riskAssessmentType];
};

export const getRiskAssessmentType = (originalLoanInfo: any, loanData: any) => {
  const originalType = originalLoanInfo?.tsum?.riskAssessmentType;
  const loanType = loanData?.underwritingMethodID
    ? getRiskAssessmentTypeString(loanData?.underwritingMethodID)
    : undefined;

  // If neither value exists, return empty object
  if (!originalType && !loanType) {
    return {};
  }

  // If only loanData value exists, return it
  if (!originalType && loanType) {
    return { tsum: { riskAssessmentType: loanType } };
  }

  // If both exist and are different, return loanData value
  if (originalType && loanType && originalType !== loanType) {
    return { tsum: { riskAssessmentType: loanType } };
  }

  // In all other cases (including both exist and are equal), return empty object
  return {};
};

export const getVaLoanData = (
  loanInformation: any,
  originalLoanInfo: any,
): Partial<VaTypes> => {
  // if value has not been set or is not applicable, return empty object
  if (
    loanInformation?.vaFundingFeeExempted ===
      originalLoanInfo?.vaLoanData?.fundingFeeExempt &&
    loanInformation?.vaFirstTimeUse ===
      originalLoanInfo?.vaLoanData?.firstTimeUse
  )
    return {};
  const vaLoanData: Record<string, any> = {};
  if (
    loanInformation?.vaFundingFeeExempted !==
    originalLoanInfo?.vaLoanData?.fundingFeeExempt
  ) {
    vaLoanData.fundingFeeExempt = loanInformation?.vaFundingFeeExempted;
  }
  if (
    loanInformation?.vaFirstTimeUse !==
    originalLoanInfo?.vaLoanData?.firstTimeUse
  ) {
    vaLoanData.firstTimeUse = loanInformation?.vaFirstTimeUse;
  }
  return Object.keys(vaLoanData).length > 0 ? { vaLoanData } : {};
};

export const getPropertyValues = (
  summaryDetails: any,
  loanProperties: any,
): Partial<PropertyValueParamsType> => {
  let propertyValueParams: PropertyValueParamsType;

  const originsAppraised = loanProperties?.propertyAppraisedValueAmount;
  const originsEstimated = loanProperties?.propertyEstimatedValueAmount;
  const originsPurchase = loanProperties?.purchasePriceAmount;

  const hasAppraised =
    originsAppraised != null && originsAppraised !== undefined;
  const hasEstimated =
    originsEstimated != null && originsEstimated !== undefined;
  const hasPurchase = originsPurchase != null && originsPurchase !== undefined;

  // Send appraised if Origin returned a value, or if all values are null (clears field)
  const shouldSendAppraised = hasAppraised || (!hasEstimated && !hasPurchase);
  // Send estimated if no appraised value from Origin, or if estimated value exists
  const shouldSendEstimated = !hasAppraised || hasEstimated;

  // we will update propertyAppraisedValueAmount and propertyEstimatedValueAmount with purchasePriceAmount if all appraised, estimated, purchasepriceamount are null in origin
  // and user has entered purchase price on search page, so we will receive this value in LockRates response.

  // eslint-disable-next-line prefer-const
  propertyValueParams = {
    ...(shouldSendAppraised
      ? {
          propertyAppraisedValueAmount:
            summaryDetails?.loan?.property?.appraisedValueAmount ||
            summaryDetails?.loan?.property?.purchasePriceAmount ||
            originsAppraised,
        }
      : {}),
    ...(shouldSendEstimated
      ? {
          propertyEstimatedValueAmount:
            summaryDetails?.loan?.property?.estimatedValueAmount ||
            summaryDetails?.loan?.property?.purchasePriceAmount ||
            summaryDetails?.loan?.property?.appraisedValueAmount ||
            originsEstimated,
        }
      : {}),
  };

  return propertyValueParams;
};
